import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, X } from "lucide-react";

/**
 * Service-row card.
 *
 * Two layout modes:
 *   • default — alternating dual-column on ≥md, image-above-text stacked on mobile.
 *               Uses `object-cover` and a fade overlay; image may be cropped to fit.
 *   • fullBleedImage — text panel ALWAYS on top (contained width), image ALWAYS below
 *               at native aspect ratio with `object-contain` (no cropping). Whole image
 *               is wrapped in <a href={ctaHref}> so the section reads as a Reserve link.
 *
 * Optional secondaryImage + secondaryCaption renders a gold-bordered thumbnail inside
 * the text panel that opens a lightbox on click.
 */
export default function ServiceModule({
  imageSrc,
  imageSide = "left",
  eyebrow,
  heading,
  body,
  promo,
  ctaHref = "/book",
  ctaLabel = "Reserve Now",
  chips,
  testid,
  secondaryImage,
  secondaryCaption,
  secondaryTestId,
  hideTextOnSmall = false,
  fullBleedImage = false,
  imagePosition = "object-center",
}) {
  const imageFirst = imageSide === "left";
  const [lightboxOpen, setLightboxOpen] = useState(false);

  /* -----------------------------------------------------------------------
     fullBleedImage layout — text on top, full-width unclipped image below.
     ----------------------------------------------------------------------- */
  if (fullBleedImage) {
    return (
      <section
        data-testid={testid}
        className="relative bg-[#0B0B0C] border-t border-white/10"
      >
        {/* TEXT PANEL (contained, above image) */}
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-14 md:py-20">
          {eyebrow && (
            <div className="text-[11px] uppercase tracking-[0.4em] text-[#8E9AA6] mb-5">
              {eyebrow}
            </div>
          )}
          <h2
            data-testid={`${testid}-heading`}
            className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white leading-[1.1] mb-6"
          >
            {heading}
          </h2>
          {promo && (
            <div data-testid={`${testid}-promo`} className="mb-6">
              <span
                data-testid={`${testid}-promo-pill`}
                className="inline-block px-3 py-1.5 text-[10px] uppercase tracking-[0.32em] text-[#E6E6E6] border border-white/25 bg-white/[0.04] mb-4"
              >
                {promo.pillLabel}
              </span>
              <p
                data-testid={`${testid}-promo-text`}
                className="italic text-base md:text-lg text-white font-light leading-relaxed border-l border-white/25 pl-4"
              >
                {promo.text}
              </p>
            </div>
          )}
          {body && (
            <p
              data-testid={`${testid}-body`}
              className="text-sm md:text-base text-[#E6E6E6] font-light leading-relaxed max-w-2xl mb-7"
            >
              {body}
            </p>
          )}
          {chips && chips.length > 0 && (
            <div
              data-testid={`${testid}-chips`}
              className="flex flex-wrap gap-2 mb-7"
            >
              {chips.map((c) => (
                <span
                  key={c}
                  className="inline-block px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-[#E6E6E6] border border-white/15 bg-white/[0.03]"
                >
                  {c}
                </span>
              ))}
            </div>
          )}
          <div className="flex items-center flex-wrap gap-5 md:gap-7">
            <Link
              to={ctaHref}
              data-testid={`${testid}-cta`}
              className="inline-flex items-center gap-2 bg-white text-black hover:bg-[#E6E6E6] px-8 py-3.5 transition-all duration-300 font-medium text-[11px] tracking-[0.28em] uppercase"
            >
              {ctaLabel} <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
            {secondaryImage && (
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                data-testid={secondaryTestId || `${testid}-secondary-thumb`}
                aria-label={secondaryCaption || "Open preview"}
                className="group relative inline-block w-[100px] h-[100px] md:w-[140px] md:h-[140px] border border-[#C5A24A]/70 hover:border-[#C5A24A] hover:ring-2 hover:ring-[#C5A24A]/40 transition-all duration-300 overflow-hidden shrink-0"
              >
                <img
                  src={secondaryImage}
                  alt={secondaryCaption || "Preview"}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  draggable={false}
                />
              </button>
            )}
          </div>
        </div>

        {/* FULL-BLEED UNCROPPED IMAGE (clickable to ctaHref) */}
        <a
          href={ctaHref}
          data-testid={`${testid}-image-link`}
          className="block w-full bg-[#0B0B0C] focus:outline-none focus:ring-1 focus:ring-[#C5A24A]/60 group"
          aria-label={`${heading} — Reserve Now`}
        >
          <img
            src={imageSrc}
            alt={heading}
            className="block w-full h-auto max-w-[1400px] mx-auto object-contain object-center transition-opacity duration-300 group-hover:opacity-95"
            loading="lazy"
            draggable={false}
          />
        </a>

        {/* LIGHTBOX */}
        {secondaryImage && lightboxOpen && (
          <Lightbox
            testid={`${testid}-secondary-lightbox`}
            src={secondaryImage}
            caption={secondaryCaption}
            onClose={() => setLightboxOpen(false)}
          />
        )}
      </section>
    );
  }

  /* -----------------------------------------------------------------------
     Default alternating dual-column layout (Modules 02 etc.)
     ----------------------------------------------------------------------- */
  return (
    <section
      data-testid={testid}
      className="relative bg-[#0B0B0C] border-t border-white/10"
    >
      {hideTextOnSmall && (
        <a
          href={ctaHref}
          data-testid={`${testid}-image-link`}
          className="block lg:hidden group focus:outline-none focus:ring-1 focus:ring-[#C5A24A]/60"
          aria-label={`${heading} — Reserve Now`}
        >
          <div className="relative w-full aspect-[16/10] sm:aspect-[16/9] overflow-hidden">
            <img
              src={imageSrc}
              alt={heading}
              className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.03]"
              loading="lazy"
              draggable={false}
            />
          </div>
        </a>
      )}

      <div
        className={`max-w-7xl mx-auto grid grid-cols-1 ${
          hideTextOnSmall ? "hidden lg:grid lg:grid-cols-2" : "md:grid-cols-2"
        } ${imageFirst ? "" : "md:[direction:rtl]"}`}
      >
        <div className={`relative h-[420px] sm:h-[480px] md:h-auto md:min-h-[560px] overflow-hidden ${imageFirst ? "" : "md:[direction:ltr]"}`}>
          <img
            src={imageSrc}
            alt={heading}
            className={`absolute inset-0 w-full h-full object-cover ${imagePosition} transition-transform duration-1000 ease-out hover:scale-[1.04]`}
            loading="lazy"
            draggable={false}
          />
          <div
            className="absolute inset-0 md:hidden"
            style={{ background: "linear-gradient(180deg, rgba(11,11,12,0.10) 0%, rgba(11,11,12,0.20) 55%, rgba(11,11,12,0.95) 100%)" }}
            aria-hidden
          />
          <div
            className="absolute inset-0 hidden md:block"
            style={{
              background: imageFirst
                ? "linear-gradient(90deg, rgba(11,11,12,0.55) 0%, rgba(11,11,12,0.25) 45%, rgba(11,11,12,0.95) 100%)"
                : "linear-gradient(-90deg, rgba(11,11,12,0.55) 0%, rgba(11,11,12,0.25) 45%, rgba(11,11,12,0.95) 100%)",
            }}
            aria-hidden
          />
        </div>
        <div className={`relative bg-[#0B0B0C] px-6 md:px-14 py-14 md:py-24 flex items-center ${imageFirst ? "" : "md:[direction:ltr]"}`}>
          <div className="max-w-xl w-full">
            {eyebrow && (
              <div className="text-[11px] uppercase tracking-[0.4em] text-[#8E9AA6] mb-5">
                {eyebrow}
              </div>
            )}
            <h2
              data-testid={`${testid}-heading`}
              className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white leading-[1.1] mb-7"
            >
              {heading}
            </h2>
            {promo && (
              <div data-testid={`${testid}-promo`} className="mb-7">
                <span data-testid={`${testid}-promo-pill`} className="inline-block px-3 py-1.5 text-[10px] uppercase tracking-[0.32em] text-[#E6E6E6] border border-white/25 bg-white/[0.04] mb-4">
                  {promo.pillLabel}
                </span>
                <p data-testid={`${testid}-promo-text`} className="italic text-base md:text-lg text-white font-light leading-relaxed border-l border-white/25 pl-4">
                  {promo.text}
                </p>
              </div>
            )}
            <p data-testid={`${testid}-body`} className="text-sm md:text-base text-[#E6E6E6] font-light leading-relaxed mb-8">
              {body}
            </p>
            {chips && chips.length > 0 && (
              <div data-testid={`${testid}-chips`} className="flex flex-wrap gap-2 mb-9">
                {chips.map((c) => (
                  <span key={c} className="inline-block px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-[#E6E6E6] border border-white/15 bg-white/[0.03]">
                    {c}
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center flex-wrap gap-5 md:gap-7">
              <Link to={ctaHref} data-testid={`${testid}-cta`} className="inline-flex items-center gap-2 bg-white text-black hover:bg-[#E6E6E6] px-8 py-3.5 transition-all duration-300 font-medium text-[11px] tracking-[0.28em] uppercase">
                {ctaLabel} <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
              {secondaryImage && (
                <button
                  type="button"
                  onClick={() => setLightboxOpen(true)}
                  data-testid={secondaryTestId || `${testid}-secondary-thumb`}
                  aria-label={secondaryCaption || "Open preview"}
                  className="group relative inline-block w-[100px] h-[100px] md:w-[140px] md:h-[140px] border border-[#C5A24A]/70 hover:border-[#C5A24A] hover:ring-2 hover:ring-[#C5A24A]/40 transition-all duration-300 overflow-hidden shrink-0"
                >
                  <img src={secondaryImage} alt={secondaryCaption || "Preview"} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" draggable={false} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {secondaryImage && lightboxOpen && (
        <Lightbox
          testid={`${testid}-secondary-lightbox`}
          src={secondaryImage}
          caption={secondaryCaption}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </section>
  );
}

function Lightbox({ testid, src, caption, onClose }) {
  return (
    <div
      data-testid={testid}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-md px-4 py-10"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid={`${testid}-close`}
        aria-label="Close preview"
        className="absolute top-6 right-6 w-11 h-11 flex items-center justify-center border border-white/30 text-white hover:border-[#C5A24A] hover:text-[#C5A24A] transition-colors"
      >
        <X size={20} strokeWidth={1.5} />
      </button>
      <figure onClick={(e) => e.stopPropagation()} className="max-w-5xl w-full">
        <div className="relative w-full overflow-hidden border border-[#C5A24A]/40">
          <img src={src} alt={caption || "Preview"} className="block w-full h-auto max-h-[78vh] object-contain bg-black" draggable={false} />
        </div>
        {caption && (
          <figcaption className="mt-5 text-center font-serif italic text-[#E6E6E6] text-base md:text-lg">
            {caption}
          </figcaption>
        )}
      </figure>
    </div>
  );
}
